import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  Chip,
  Grid,
  IconButton,
  List,
  ListItem,
  Typography, ListItemButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ModalConfirmDelete from 'components/modal-confirm-delete';
import { ListingEntry } from 'graphql/listings/queries';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PartyItemList } from '../hooks';
import { formatRoleName, getInitials, uniqueByName, uniqueKinds } from '../utils';
import PartyCompaniesText from './PartyCompaniesText';

const useStyles = makeStyles((theme: any) => ({
  list: {
    padding: 0,
  },
  card: {
    position: 'relative',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.card}`,
    borderRadius: 4,
    boxShadow: 'none',
    transition: theme.transitions.create(['background-color', 'border-color']),
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '&:focus-within $deleteButton': {
      opacity: 1,
    },
  },
  cardSelected: {
    backgroundColor: theme.palette.background.ligth,
    borderLeft: `2px solid ${theme.accent.main}`,
    '&:hover': {
      backgroundColor: theme.palette.background.ligth,
    },
  },
  cardAction: {
    display: 'block',
    height: '100%',
    padding: theme.spacing(2),
  },
  cardContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    paddingRight: theme.spacing(4),
  },
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: theme.spacing(7),
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.neutraldivider}`,
    cursor: 'pointer',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: theme.palette.background.default,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.background.ligth,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.background.ligth,
    },
    '&.Mui-selected::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: 2,
      backgroundColor: theme.accent.main,
    },
    '&:focus-within $deleteButton': {
      opacity: 1,
    },
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.dualText.primary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 500,
    [theme.breakpoints.down('md')]: {
      width: theme.spacing(5),
      height: theme.spacing(5),
    },
  },
  info: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  name: {
    color: theme.palette.primary.darker,
    fontWeight: 500,
    overflowWrap: 'anywhere',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  chip: {
    height: 22,
    color: theme.palette.text.secondary,
    borderColor: theme.palette.border.card,
    fontSize: theme.typography.caption.fontSize,
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  deleteButton: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
    '&:hover': {
      color: theme.palette.error.main,
    },
    [theme.breakpoints.up('sm')]: {
      opacity: 0,
    },
    '@media (hover: none)': {
      opacity: 1,
    },
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
  cardDelete: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    zIndex: 1,
  },
}));

type PartyItemProps = {
  parties: PartyItemList[];
  handleDeleteAssociation: (partyId: string, kind: string, onSuccess?: () => void) => void;
  listingEntries?: ListingEntry[];
  handleViewChange: (id: string) => void;
  kind?: string;
  variant?: 'card' | 'row';
  selectedId?: string;
  isDeleting?: boolean;
};

const PartyItem: FC<PartyItemProps> = ({
  parties,
  handleDeleteAssociation,
  listingEntries = [],
  handleViewChange,
  kind = '',
  variant = 'row',
  selectedId,
  isDeleting = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [pendingDelete, setPendingDelete] = useState<PartyItemList | null>(null);

  const canDelete = !!kind && listingEntries.some((entry) => entry.code === kind);

  const renderContent = (party: PartyItemList) => {
    const members = uniqueByName(party.member_of);
    const kinds = uniqueKinds(party.kinds);

    return (
      <>
        <Avatar className={classes.avatar}>{getInitials(party.name)}</Avatar>
        <Box className={classes.info}>
          <Typography variant="body2" className={classes.name}>
            {party.name}
          </Typography>
          {members.length > 0 && <PartyCompaniesText members={members} />}
          {!kind && kinds.length > 0 && (
            <Box className={classes.chips}>
              {kinds.map((role) => (
                <Chip
                  key={role}
                  size="small"
                  variant="outlined"
                  label={formatRoleName(role)}
                  className={classes.chip}
                />
              ))}
            </Box>
          )}
        </Box>
      </>
    );
  };

  const renderDelete = (party: PartyItemList) => {
    if (!canDelete) return null;

    return (
      <IconButton
        className={clsx(classes.deleteButton, variant === 'card' && classes.cardDelete)}
        aria-label={t('parties:remove-party-aria', { name: party.name, role: formatRoleName(kind) })}
        onClick={(event) => {
          event.stopPropagation();
          setPendingDelete(party);
        }}
        size="large"
      >
        <FontAwesomeIcon icon="trash" />
      </IconButton>
    );
  };

  const confirmDialog = (
    <ModalConfirmDelete
      open={!!pendingDelete}
      handleClose={() => setPendingDelete(null)}
      title={t('parties:remove-party-title')}
      subtitle={
        pendingDelete
          ? t('parties:remove-party-subtitle', { name: pendingDelete.name, role: formatRoleName(kind) })
          : ''
      }
      textCancel={t('common:cancel')}
      textConfirm={t('common:remove')}
      isLoading={isDeleting}
      handleDelete={() => {
        if (!pendingDelete) return;
        handleDeleteAssociation(pendingDelete.id, kind, () => setPendingDelete(null));
      }}
    />
  );

  if (variant === 'card') {
    return (
      <>
        <Grid container spacing={2}>
          {parties.map((party) => {
            const isSelected = party.id === selectedId;

            return (
              <Grid item xs={12} sm={6} key={party.id}>
                <Card
                  elevation={0}
                  className={clsx(classes.card, isSelected && classes.cardSelected)}
                >
                  <CardActionArea
                    className={classes.cardAction}
                    onClick={() => handleViewChange(party.id)}
                    aria-current={isSelected ? 'true' : undefined}
                  >
                    <Box className={classes.cardContent}>{renderContent(party)}</Box>
                  </CardActionArea>
                  {renderDelete(party)}
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {confirmDialog}
      </>
    );
  }

  return (
    <>
      <List disablePadding className={classes.list}>
        {parties.map((party) => (
          <ListItemButton
            key={party.id}
            selected={party.id === selectedId}
            className={classes.row}
            aria-current={party.id === selectedId ? 'true' : undefined}
            onClick={() => handleViewChange(party.id)}
          >
            <Box className={classes.rowContent}>{renderContent(party)}</Box>
            {renderDelete(party)}
          </ListItemButton>
        ))}
      </List>
      {confirmDialog}
    </>
  );
};

export default PartyItem;
